import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiDelete, apiGet, apiPost, apiPut } from '../../shared/api/client';
import { useAppStore } from '../../shared/stores/appStore';
import { useValidatedBabyId } from '../../shared/hooks/useValidatedBabyId';
import { findOpenSleep, suggestSleepCategory } from './sleepShape';

/**
 * Server-backed "sleep in progress" (docs/ux-modernisation-spec.md §0.1, §3).
 *
 * The open SLEEP event IS the timer. There is no zustand timer for sleep any
 * more — `useOpenSleep` derives the running state from the SLEEP type query
 * (Dexie-served through the offline router, so it works offline and includes
 * not-yet-pushed `local-` rows) and ticks once a second only while a sleep is
 * open. `useSleepActions` is the only writer: start = POST an open event,
 * wake = PUT `endTime` + the FULL `data` object (the server replaces `data`
 * wholesale — a partial patch would wipe `category`/`notes`), cancel = DELETE.
 *
 * Every action reads the open sleep FRESH from the query cache at call time
 * (never from a render-time object): after the sync engine rewrites
 * `local-<uuid>` to the server id the cache refetches, and a wake that still
 * held the stale `local-` id would otherwise address the wrong row.
 *
 * Actions return the mutation's promise (resolving with the saved row) and
 * REJECT on failure, so a sheet can stay open and show the error. A tap that
 * lands while another action is in flight — or that repeats the SAME action
 * within the cooldown after it settled — is ignored and resolves to
 * `undefined` (same idea as useSaveGuard).
 */

const COOLDOWN_MS = 800;
const TICK_MS = 1000;

export const sleepQueryKey = (babyId) => ['events', babyId, 'SLEEP'];

// Module-level so react-query can memoise the selected array between renders.
const selectRows = (res) => res?.items || res?.events || res || [];

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const stripLocal = (id) =>
  typeof id === 'string' && id.startsWith('local-') ? id.slice('local-'.length) : id;

export function useOpenSleep(babyId) {
  const { data, isLoading } = useQuery({
    queryKey: sleepQueryKey(babyId),
    queryFn: () => apiGet(`/babies/${babyId}/events`, { type: 'SLEEP' }),
    enabled: !!babyId,
    select: selectRows,
  });
  const events = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const [now, setNow] = useState(() => Date.now());
  const openSleep = useMemo(() => findOpenSleep(events, now), [events, now]);
  const openId = openSleep ? openSleep.eventId : null;

  // 1 s tick only while a sleep is open. Re-seed `now` when the open sleep
  // changes so elapsed time is right on the very first render, not a tick later.
  useEffect(() => {
    if (!openId) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [openId]);

  const elapsedMs = openSleep
    ? Math.max(0, now - Date.parse(openSleep.startTime))
    : 0;

  return {
    openSleep,
    elapsedMs,
    elapsedMinutes: Math.floor(elapsedMs / 60000),
    isLoading: !!isLoading,
    events,
  };
}

export function useSleepActions(babyId) {
  const queryClient = useQueryClient();
  const inFlight = useRef(false);
  const settledAt = useRef({});
  const [isSaving, setIsSaving] = useState(false);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['events'] });
    // Any sleep mutation changes today's predicted schedule (naps taken, anchor).
    // This fires on the LOCAL write, so the refetch usually races the push and
    // the server answers from the rows it held a moment ago; the sync engine
    // invalidates ['sleep-schedule'] again once the drain lands (bug 2, v1.4.3)
    // and SleepSchedule folds the client's open sleep into its rows meanwhile.
    queryClient.invalidateQueries({ queryKey: ['sleep-schedule'] });
  }, [queryClient]);

  /**
   * Freshest row for an action, read from the cache at call time.
   *
   * With an `eventId`: the cached row under that id (exact, then with `local-`
   * stripped — the server id is deterministic, so a row the push has already
   * rewritten is found this way); if it is not cached at all, a bare
   * `{ eventId }` so the router can still resolve it. Never a *different* row —
   * a cancel or a start-time change must only ever touch the row it was asked
   * about.
   *
   * Without an `eventId`: the newest open sleep ("wake whatever is asleep").
   */
  const resolveRow = useCallback(
    (eventId) => {
      const rows = selectRows(queryClient.getQueryData(sleepQueryKey(babyId)));
      const list = Array.isArray(rows) ? rows : [];
      if (eventId) {
        const exact = list.find((r) => r.eventId === eventId);
        if (exact) return exact;
        const stripped = stripLocal(eventId);
        if (stripped !== eventId) {
          const rewritten = list.find((r) => r.eventId === stripped);
          if (rewritten) return rewritten;
        }
        return { eventId, data: {} };
      }
      return findOpenSleep(list);
    },
    [queryClient, babyId]
  );

  // Offline/timeout errors are never toasted (constraints.md rule 41): the
  // router's "still syncing" NetworkError and a genuine offline blip are both
  // transient — the rejection still carries `err.message`, so a sheet can show
  // it inline and the user simply tries again. Everything else is toasted.
  const reportError = useCallback((fallback) => (err) => {
    if (err?.isNetworkError) return;
    // An HTTP error (rare: only when a real-id row is missing locally and the
    // router falls through to the network) was already toasted with the
    // server's message by api/client.js — do not toast it twice.
    if (err?.response) return;
    toast.error(err?.message || fallback);
  }, []);

  const startMutation = useMutation({
    mutationFn: ({ startTime, category, notes } = {}) => {
      const start = toDate(startTime) || new Date();
      return apiPost(`/babies/${babyId}/events`, {
        type: 'SLEEP',
        startTime: start.toISOString(),
        endTime: null,
        data: {
          category: category || suggestSleepCategory(start),
          notes: notes || '',
        },
      });
    },
    onSuccess: invalidate,
    onError: reportError('Could not start the sleep — please try again'),
  });

  const wakeMutation = useMutation({
    mutationFn: ({ eventId, endTime, category, notes } = {}) => {
      const row = resolveRow(eventId);
      if (!row) throw new Error('No sleep in progress');
      const end = toDate(endTime) || new Date();
      const start = Date.parse(row.startTime || row.createdAt);
      if (Number.isNaN(start)) throw new Error('This sleep has no start time');
      if (end.getTime() <= start) throw new Error('Woke-up time must be after the fell-asleep time');
      const existing = row.data || {};
      return apiPut(`/babies/${babyId}/events/${row.eventId}`, {
        endTime: end.toISOString(),
        data: {
          ...existing,
          category: category ?? existing.category ?? 'nap',
          notes: notes ?? existing.notes ?? '',
          durationMinutes: Math.max(1, Math.round((end.getTime() - start) / 60000)),
        },
      });
    },
    onSuccess: invalidate,
    onError: reportError('Could not save the sleep — please try again'),
  });

  const adjustStartMutation = useMutation({
    mutationFn: ({ eventId, startTime } = {}) => {
      const row = resolveRow(eventId);
      if (!row) throw new Error('No sleep in progress');
      const start = toDate(startTime);
      if (!start) throw new Error('Choose a valid fell-asleep time');
      const body = { startTime: start.toISOString() };
      // A closed sleep keeps its derived duration in step with the new start.
      // (The server replaces `data` wholesale, so send the whole object.)
      if (row.endTime) {
        const end = Date.parse(row.endTime);
        if (!Number.isNaN(end) && end <= start.getTime()) {
          throw new Error('Fell-asleep time must be before the woke-up time');
        }
        if (!Number.isNaN(end)) {
          body.data = {
            ...(row.data || {}),
            durationMinutes: Math.max(1, Math.round((end - start.getTime()) / 60000)),
          };
        }
      }
      return apiPut(`/babies/${babyId}/events/${row.eventId}`, body);
    },
    onSuccess: invalidate,
    onError: reportError('Could not change the start time — please try again'),
  });

  const metaMutation = useMutation({
    mutationFn: ({ eventId, category, notes } = {}) => {
      const row = resolveRow(eventId);
      if (!row) throw new Error('No sleep in progress');
      const existing = row.data || {};
      return apiPut(`/babies/${babyId}/events/${row.eventId}`, {
        data: {
          ...existing,
          category: category ?? existing.category ?? 'nap',
          notes: notes ?? existing.notes ?? '',
        },
      });
    },
    onSuccess: invalidate,
    onError: reportError('Could not update the sleep — please try again'),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ eventId } = {}) => {
      const row = resolveRow(eventId);
      if (!row) throw new Error('No sleep in progress');
      return apiDelete(`/babies/${babyId}/events/${row.eventId}`);
    },
    onSuccess: invalidate,
    onError: reportError('Could not discard the sleep — please try again'),
  });

  // One shared in-flight lock across the four record-lifecycle actions (start,
  // wake, adjust, cancel — `updateSleepMeta` below is serialised instead of
  // guarded): a wake and a cancel must never run at the same time. The COOLDOWN_MS that follows a settled mutation
  // is deliberately PER ACTION — it exists to swallow a double tap on the SAME
  // button (a second Start must never create two open sleeps; the router
  // resolves in milliseconds, long before the push), not to freeze the hook.
  // A shared cooldown silently ate the Undo on the "Nap started" toast, which
  // is rendered the instant the start settles: the toast was dismissed, the
  // cancel dropped, and the unwanted sleep kept running. Same for WakeUpSheet's
  // Discard when it followed another action inside the window.
  const guard = useCallback((key, mutateAsync) => async (args) => {
    if (!babyId || inFlight.current) return undefined;
    if (Date.now() - (settledAt.current[key] || 0) < COOLDOWN_MS) return undefined;
    inFlight.current = true;
    setIsSaving(true);
    try {
      return await mutateAsync(args);
    } finally {
      inFlight.current = false;
      settledAt.current[key] = Date.now();
      setIsSaving(false);
    }
  }, [babyId]);

  const startSleep = useMemo(() => guard('start', startMutation.mutateAsync), [guard, startMutation.mutateAsync]);
  const wakeSleep = useMemo(() => guard('wake', wakeMutation.mutateAsync), [guard, wakeMutation.mutateAsync]);
  const adjustSleepStart = useMemo(
    () => guard('adjustStart', adjustStartMutation.mutateAsync),
    [guard, adjustStartMutation.mutateAsync]
  );
  // Meta (category + notes) is the one action that is SERIALISED rather than
  // guarded. Category and notes are two controls writing ONE record, and
  // tapping the category control blurs the notes textarea first — two meta
  // writes about 50 ms apart. Under the drop-guard the second one returned
  // `undefined` and vanished: the segmented control has no optimistic state,
  // so it simply did not move, with no toast and no error, and the sleep
  // stayed a Nap until the user tapped again a second later. A meta write
  // neither creates nor closes a record, so a second one is a correction, not
  // a duplicate — queue it behind the first and let the last writer win.
  const metaChain = useRef(Promise.resolve());
  const updateSleepMeta = useCallback(
    (args) => {
      if (!babyId) return Promise.resolve(undefined);
      const run = metaChain.current
        .catch(() => {})
        .then(() => metaMutation.mutateAsync(args));
      metaChain.current = run.catch(() => {});
      return run;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [babyId, metaMutation.mutateAsync]
  );
  const cancelSleep = useMemo(() => guard('cancel', cancelMutation.mutateAsync), [guard, cancelMutation.mutateAsync]);

  return { startSleep, wakeSleep, adjustSleepStart, updateSleepMeta, cancelSleep, isSaving };
}

/**
 * One-off migration for users upgrading with the old zustand SLEEP timer
 * still running: POST an open sleep with its `startedAt`, then clear the
 * timer so the open event takes over. Mount once in Layout. Runs at most once
 * per app load; if the write fails the timer is left in place so the next
 * launch tries again.
 */
export function useLegacySleepTimerMigration() {
  const queryClient = useQueryClient();
  const activeTimer = useAppStore((s) => s.activeTimer);
  const stopTimer = useAppStore((s) => s.stopTimer);
  const { selectedBabyId: babyId } = useValidatedBabyId();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    if (!babyId || activeTimer?.type !== 'SLEEP') return;
    attempted.current = true;

    const startedAt = Number(activeTimer.startedAt);
    const start = new Date(Number.isNaN(startedAt) || startedAt <= 0 ? Date.now() : startedAt);
    const category = activeTimer.meta?.category || suggestSleepCategory(start);
    const notes = typeof activeTimer.meta?.notes === 'string' ? activeTimer.meta.notes : '';

    (async () => {
      try {
        await apiPost(`/babies/${babyId}/events`, {
          type: 'SLEEP',
          startTime: start.toISOString(),
          endTime: null,
          data: { category, notes },
        });
      } catch {
        // Transient (Dexie unavailable) — keep the legacy timer and retry on
        // the next app load rather than silently dropping a running sleep.
        return;
      }
      stopTimer();
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['sleep-schedule'] });
    })();
  }, [activeTimer, babyId, queryClient, stopTimer]);
}
